/**
 * Shared fine-grained authorization (FGA) primitives used by BOTH the platform
 * API and the MCP server, so human users and delegated agents are filtered
 * identically.
 *
 * Model: row visibility is gated at the **container** level (entity, department,
 * cost center, …) — never per individual row (WorkOS high-cardinality guidance).
 * At query time we resolve the set of container ids a principal can access and
 * push it into the query `WHERE` clause. Org-wide permission holders short-circuit
 * to `unrestricted` (no FGA call, no predicate).
 *
 * This module is pure types/constants — no IO, no framework code.
 */

/**
 * Boundary dimensions that can gate row visibility. Which ones are actually
 * enforced is configured **per tenant** via {@link DataVisibilityPolicy} — they
 * are never hardcoded on. `entity`/`department`/`cost_center` have a manager
 * model today (`ap_department.head_user_id`, `ap_cost_center.owner_user_id`);
 * `managerial_account`/`segment` require an ownership/grant model before they
 * can be enabled (tracked, not yet enforceable).
 */
export const AuthzContainerType = {
  ENTITY: 'entity',
  DEPARTMENT: 'department',
  // `cost-center` (hyphen) matches the WorkOS FGA resource-type slug, which is
  // auto-slugified from the "Cost Center" display name. This value is the
  // FGA/authorization identifier only — the DB column/relation stays `cost_center`.
  COST_CENTER: 'cost-center',
  BANK_ACCOUNT: 'bank-account',
  GL_ACCOUNT: 'gl-account',
  MANAGERIAL_ACCOUNT: 'managerial_account',
  SEGMENT: 'segment',
} as const;
export type AuthzContainerType = (typeof AuthzContainerType)[keyof typeof AuthzContainerType];

/** All supported container dimensions (frozen, for iteration/validation). */
export const AUTHZ_CONTAINER_TYPES = Object.freeze(
  Object.values(AuthzContainerType),
) as readonly AuthzContainerType[];

/**
 * Container dimensions that have a manager source-of-truth today and can be
 * enforced now. Others remain in the catalog but are not yet enforceable.
 */
export const ENFORCEABLE_AUTHZ_CONTAINER_TYPES = Object.freeze([
  AuthzContainerType.ENTITY,
  AuthzContainerType.DEPARTMENT,
  AuthzContainerType.COST_CENTER,
  AuthzContainerType.BANK_ACCOUNT,
  AuthzContainerType.GL_ACCOUNT,
]) as readonly AuthzContainerType[];

/**
 * FGA relations on a container. v1 populates `MANAGER` only (managed-scope:
 * head/owner of the unit). `VIEWER` is reserved for explicit grants in a later
 * phase — defined now so the FGA model and code accept grants without a refactor.
 */
export const AuthzRelation = {
  MANAGER: 'manager',
  VIEWER: 'viewer',
} as const;
export type AuthzRelation = (typeof AuthzRelation)[keyof typeof AuthzRelation];

/**
 * Per-tenant data-visibility policy: which container dimensions actually gate
 * visibility for this tenant. Empty ⇒ tenant-wide visibility (default; preserves
 * pre-FGA behavior and enables zero-risk progressive rollout).
 */
export interface DataVisibilityPolicy {
  enabledContainers: AuthzContainerType[];
}

export const DEFAULT_DATA_VISIBILITY_POLICY: DataVisibilityPolicy = Object.freeze({
  enabledContainers: [],
});

/** True when the tenant policy enforces at least one container dimension. */
export function isVisibilityFilteringEnabled(policy: DataVisibilityPolicy): boolean {
  return policy.enabledContainers.length > 0;
}

/**
 * Resolved scope filter for a single container dimension.
 * - `unrestricted` ⇒ no row filtering on this dimension (org-wide permission,
 *   fast path, or the dimension is disabled for the tenant).
 * - `restricted` ⇒ only rows whose container id is in `containerIds` are visible
 *   (an empty list means the principal can see nothing on this dimension).
 */
export type ContainerScopeFilter =
  | { readonly kind: 'unrestricted' }
  | { readonly kind: 'restricted'; readonly containerIds: readonly string[] };

export const UNRESTRICTED_SCOPE: ContainerScopeFilter = Object.freeze({ kind: 'unrestricted' });

export function restrictedScope(containerIds: readonly string[]): ContainerScopeFilter {
  return Object.freeze({ kind: 'restricted', containerIds: Object.freeze([...containerIds]) });
}

/**
 * Full set of resolved scope filters for a principal+tenant, keyed by container
 * dimension. The query layer applies each enabled dimension's filter as an
 * additional `WHERE` predicate (AND). Missing dimensions are treated as
 * `unrestricted`.
 */
export interface AuthorizationScopeFilters {
  readonly byContainer: Partial<Record<AuthzContainerType, ContainerScopeFilter>>;
}

export const UNRESTRICTED_SCOPE_FILTERS: AuthorizationScopeFilters = Object.freeze({
  byContainer: Object.freeze({}),
});

/** Resolve the effective filter for one dimension (defaults to unrestricted). */
export function scopeFilterFor(
  filters: AuthorizationScopeFilters,
  container: AuthzContainerType,
): ContainerScopeFilter {
  return filters.byContainer[container] ?? UNRESTRICTED_SCOPE;
}

// ---------------------------------------------------------------------------
// FGA slug conventions (single source of truth for the WorkOS FGA model).
// The setup script creates these slugs in WorkOS; the reconciler assigns the
// manager role; the scope service queries the view permission. Keeping them in
// code (not hardcoded strings scattered around) keeps the model in sync.
// ---------------------------------------------------------------------------

/**
 * Custom WorkOS resource type that owns tenant-wide ERP grants and parents
 * lower-cardinality visibility containers such as departments and cost centers.
 */
export const AUTHZ_TENANT_RESOURCE_TYPE = 'tenant' as const;

/**
 * Tenant-wide payroll confidentiality is intentionally represented by one
 * policy on the existing tenant resource. Payroll runs, employees and their
 * derived financial artefacts can then share the same low-cardinality FGA
 * decision; we do not create a WorkOS resource for every payslip or vendor.
 */
export const PAYROLL_FGA_RESOURCE_TYPE = AUTHZ_TENANT_RESOURCE_TYPE;
export const PAYROLL_FGA_PERMISSION_SLUG = 'payroll:view' as const;
export const PAYROLL_FGA_VIEWER_ROLE_SLUG = 'payroll-viewer' as const;
/** Narrow tenant-membership role for people who may administer FGA policies. */
export const FGA_MANAGEMENT_PERMISSION_SLUG = 'fga:manage' as const;
export const FGA_MANAGER_ROLE_SLUG = 'fga-manager' as const;
/** Legacy payroll policy target, retained only during the DB-policy cutover. */
export const PAYROLL_DATA_VISIBILITY_FLAG_SLUG = 'data-visibility-payroll' as const;
/** Rollout gate for offering payroll protection configuration to a tenant. */
export const PAYROLL_DATA_VISIBILITY_ROLLOUT_FLAG_SLUG = 'data-visibility-payroll-rollout' as const;

/**
 * Legacy authorization root retained only while Track 2 migrates grants. The
 * runtime resolver reads both roots during cutover; new grants belong on
 * {@link AUTHZ_TENANT_RESOURCE_TYPE}.
 */
export const AUTHZ_LEGACY_ORGANIZATION_RESOURCE_TYPE = 'organization' as const;

/** FGA permission slug that grants read visibility on a container (e.g. `department:view`). */
export function containerViewPermissionSlug(container: AuthzContainerType): string {
  return `${container}:view`;
}

/**
 * Bank-account movement visibility is deliberately narrower than the ordinary
 * bank-account view. The latter permits monthly balance summaries; this
 * permission is required for statements, transaction drill-down and
 * reconciliation.
 */
export function bankAccountTransactionsViewPermissionSlug(): string {
  return 'bank-account:transactions:view';
}

/**
 * FGA role slug assigned to a container's manager/head/owner (e.g.
 * `department-manager`). WorkOS role slugs allow only lowercase letters,
 * numbers, hyphens and underscores (NO colons — unlike permission slugs), so we
 * use a hyphen separator here.
 */
export function containerManagerRoleSlug(container: AuthzContainerType): string {
  return `${container}-manager`;
}

/** FGA role slug assigned to explicit viewers of a protected container. */
export function containerViewerRoleSlug(container: AuthzContainerType): string {
  return `${container}-viewer`;
}

/** Explicit bank-account role for balance-only access. */
export function bankAccountBalanceViewerRoleSlug(): string {
  return 'bank-account-balance-viewer';
}
